import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_SERIENDRUCK } from "../makros";
import { FELDER_NICHT_LESBAR, felderMit, readFields } from "./felder";

export const lektionSeriendruckRegeln: Lesson = {
  id: "regeln",
  title: "Regeln und gefilterte Empfänger",
  summary: "Nicht jeder Brief ist gleich: Bedingungen im Text und eine Auswahl der Empfänger.",
  durationMinutes: 10,
  learningGoals: [
    "Mit der Regel „Wenn … Dann … Sonst“ Text abhängig von einem Feld einsetzen",
    "Die Empfängerliste filtern, statt die Datenquelle zu verändern",
  ],
  steps: [
    {
      id: "wenn-dann",
      title: "Wenn … Dann … Sonst",
      explanation: [
        "„Sendungen“ → „Regeln“ → „Wenn … Dann … Sonst …“ fügt Text abhängig von einem " +
          "Feld ein: Ist „Kundenstatus“ gleich „Premium“, steht ein Satz über das " +
          "Treueprogramm im Brief, sonst ein anderer.",
        "Im Dokument entsteht dabei ein IF-Feld. Mit Alt + F9 wird es sichtbar: " +
          "{ IF { MERGEFIELD Kundenstatus } = \"Premium\" \"…\" \"…\" }. Wer sich an die " +
          "Schreibweise gewöhnt hat, kann solche Felder auch direkt bearbeiten.",
        "Unter „Regeln“ stehen weitere Felder, etwa „Datensatz überspringen wenn“ oder " +
          "„Nächster Datensatz“ – letzteres braucht man für Etiketten, siehe nächste " +
          "Lektion.",
      ],
      keyPoints: [
        "Sendungen → Regeln → Wenn … Dann … Sonst.",
        "Alt + F9 zeigt die Feldfunktion.",
      ],
      task: {
        instruction: "Füge eine Regel „Wenn … Dann … Sonst …“ ein, die sich auf ein Feld deiner Datenquelle bezieht.",
        hint: "Sendungen → Regeln → Wenn … Dann … Sonst …, Feldname wählen, Vergleich und beide Texte eintragen.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) return FELDER_NICHT_LESBAR;
          const regeln = felderMit(felder, "IF");
          const mitFeld = regeln.filter((feld) => /MERGEFIELD/i.test(feld.code));
          return fromCriteria(
            [
              criterion(`Eine Wenn-Regel ist eingefügt (gefunden: ${regeln.length})`, regeln.length > 0),
              criterion("Die Regel bezieht sich auf ein Seriendruckfeld", mitFeld.length > 0),
            ],
            {
              success: "Der Brief entscheidet jetzt für jeden Empfänger selbst.",
              failure: "Die Regel fehlt noch oder bezieht sich auf kein Feld.",
              hint: "Die Regel über „Sendungen → Regeln“ einfügen, nicht als getippten Text.",
            },
          );
        },
      },
    },
    {
      id: "empfaenger-filtern",
      title: "Empfänger filtern",
      explanation: [
        "Soll der Brief nur an einen Teil der Liste gehen – etwa alle Empfänger aus einer " +
          "Stadt –, ist die Versuchung groß, die Excel-Liste zu kürzen. Dann fehlt die " +
          "Hälfte, wenn der nächste Brief an alle gehen soll.",
        "„Sendungen“ → „Empfängerliste bearbeiten“ → „Filtern“ legt stattdessen eine " +
          "Bedingung fest, die nur für diesen Seriendruck gilt: Feld „Ort“, Vergleich " +
          "„Gleich“, Wert „Berlin“. Die Datenquelle bleibt unverändert.",
      ],
      keyPoints: [
        "Empfängerliste bearbeiten → Filtern.",
        "Die Datenquelle bleibt vollständig.",
      ],
      task: {
        instruction: "Filtere die Empfängerliste über „Empfängerliste bearbeiten“ → „Filtern“ nach einer Bedingung.",
        hint: "Sendungen → Empfängerliste bearbeiten → unten „Filtern“ → Feld, Vergleich und Wert eintragen.",
        macro: MAKRO_SERIENDRUCK,
        check: () =>
          pruefeMitMakro(MAKRO_SERIENDRUCK, (bericht) => {
            const abfrage = bericht.text("abfrage") ?? "";
            return fromCriteria(
              [
                criterion("Eine Datenquelle ist verbunden", bericht.janein("datenquelle") === true),
                criterion("Die Empfängerliste ist gefiltert", /\bWHERE\b/i.test(abfrage)),
              ],
              {
                success: "Gefiltert – und die Liste selbst bleibt vollständig.",
                failure: "Die Empfängerliste ist noch nicht gefiltert.",
                hint: "Einzelne Häkchen in der Liste zu entfernen ist kein Filter. Nutze „Filtern“ unter der Liste.",
              },
            );
          }),
      },
    },
  ],
};
